import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Box, CircularProgress, Grid, Icon } from '@mui/material'
import { isNil } from 'lodash'
import { toast } from 'react-toastify'
import { aquaFetch } from 'common/utils/ActionUtils'
import ApplicationConf from 'conf/ApplicationConf'
import { CardTable, SORT } from 'common/components/datatable/Table'
import { ALERT_RED, ALERT_YELLOW, getBranchColor } from 'dashboard/utils/DashboardUtils'
import { getFullDate, shortenHumanize } from 'common/utils/DateUtil'
import { mainBlue, darkBlue } from 'app/theme'
import { PASTEL_COLOR } from 'common/utils/ColorUtil'
import { cronToHuman } from 'common/utils/CronUtil'
import { getTruncate } from 'common/utils/StringUtil'
import { getJobTypeLabel } from 'dashboard/utils/JobFormats'
import { getStatusJob } from 'dashboard/utils/JobStatuses'

const JOB_CATEGORY = {
    NOT_LAUNCHED: 'notLaunchedJobs',
    ERROR: 'errorJobs',
    NO_LOGS: 'noLogsJobs',
    TOO_LONG: 'tooLongJobs',
    FULL_DIR: 'fullDirectories',
}

const JOB_CATEGORIES = [
    { value: JOB_CATEGORY.ERROR, label: 'Jobs en erreur' },
    { value: JOB_CATEGORY.NOT_LAUNCHED, label: 'Jobs non lancés' },
    { value: JOB_CATEGORY.NO_LOGS, label: 'Jobs sans logs' },
    { value: JOB_CATEGORY.FULL_DIR, label: 'Répertoires remplis' },
    { value: JOB_CATEGORY.TOO_LONG, label: 'Jobs trop longs' },
]

const buildInstanceCommonRow = (instance) => ({
    instance: {
        value: instance.name,
        fontColor: mainBlue,
    },
})

const COMMON_HEADERS = [
    { key: 'instance', value: 'Instance' },
]

const JOB_HEADERS_BASE = [
    ...COMMON_HEADERS,
    { key: 'jobType', value: 'Type' },
    { key: 'name', value: 'Nom' },
]

const NOT_LAUNCHED_HEADERS = [
    ...JOB_HEADERS_BASE,
    { key: 'cron', value: 'Programmation' },
    { key: 'lastExecution', value: 'Dernière exécution' },
]

const ERROR_HEADERS = [
    ...COMMON_HEADERS,
    { key: 'jobType', value: 'Type' },
    { key: 'branche', value: 'Branche' },
    { key: 'errorCount', value: 'Nb Err' },
    { key: 'lastError', value: 'Dernier message d\'erreur' },
    { key: 'name', value: 'Nom' },
    { key: 'duration', value: 'Durée' },
    { key: 'lastExecution', value: 'Dernière exécution' },
    { key: 'cron', value: 'Programmation' },
]

const NO_LOGS_HEADERS = [
    ...JOB_HEADERS_BASE,
    { key: 'duration', value: 'Durée' },
    { key: 'lastExecution', value: 'Dernière exécution' },
]

const TOO_LONG_HEADERS = [
    ...JOB_HEADERS_BASE,
    { key: 'duration', value: 'Durée' },
    { key: 'lastExecution', value: 'Dernière exécution' },
]

const FULL_DIR_HEADERS = [
    ...COMMON_HEADERS,
    { key: 'path', value: 'Répertoire' },
    { key: 'count', value: 'Nombre de fichiers' },
]

const buildStatusCell = job => {
    if (isNil(job.status)) return { value: '' }
    const { label, icon, color } = getStatusJob(job.status)
    return {
        value: icon ? <Icon sx={{ color }}>{icon}</Icon> : label,
        sortValue: job.status,
        tooltip: label,
    }
}

const buildJobRow = (instance, job) => ({
    ...buildInstanceCommonRow(instance),
    branche: {
        value: instance.branch ?? '',
        sortValue: instance.branch ?? '',
        fontColor: getBranchColor(instance.branch),
    },
    jobType: { value: job.jobType ? getJobTypeLabel(job.jobType) : 'INCONNU' },
    name: {
        value: job.name ?? `<Sans nom> (#${job.id})`,
        sortValue: job.name ?? '',
    },
    // Programmation tronquée ; le texte complet + le cron brut restent visibles au survol.
    cron: {
        value: getTruncate(cronToHuman(job.cron), 40, true),
        sortValue: job.cron ?? '',
        tooltip: job.cron ? <span style={{ whiteSpace: 'pre-line' }}>{`${cronToHuman(job.cron)}\n${job.cron}`}</span> : undefined,
    },
    status: buildStatusCell(job),
    errorCount: {
        value: isNil(job.errorCountSinceLastSuccess) ? '' : job.errorCountSinceLastSuccess,
        sortValue: job.errorCountSinceLastSuccess ?? 0,
    },
    lastError: {
        value: getTruncate(job.lastError, 100),
        sortValue: job.lastError ?? '',
    },
    lastExecution: {
        value: job.lastExecution ? getFullDate(job.lastExecution) : '',
        sortValue: job.lastExecution ?? 0,
    },
    duration: {
        value: isNil(job.duration) ? '' : shortenHumanize(job.duration * 1000),
        sortValue: job.duration ?? 0,
    },
    url: `${instance.url}/#/import/${job.id}`,
    instanceId: instance.instanceId,
    jobId: job.id,
    jobExecutionId: job.jobExecutionId,
})

const buildDirectoryRow = (instance, dir) => ({
    ...buildInstanceCommonRow(instance),
    path: { value: dir.path },
    count: { value: dir.count, sortValue: dir.count },
    url: instance.url,
})

const TABLE_CONFIGS = {
    [JOB_CATEGORY.NOT_LAUNCHED]: {
        title: 'Jobs non lancés',
        color: darkBlue,
        headers: NOT_LAUNCHED_HEADERS,
        defaultSort: { column: 'lastExecution', direction: SORT.DESC },
        size: 6,
    },
    [JOB_CATEGORY.ERROR]: {
        title: 'Jobs en erreur',
        color: ALERT_RED,
        headers: ERROR_HEADERS,
        defaultSort: { column: 'lastExecution', direction: SORT.DESC },
        size: 12,
    },
    [JOB_CATEGORY.NO_LOGS]: {
        title: 'Jobs sans logs',
        color: ALERT_YELLOW,
        headers: NO_LOGS_HEADERS,
        defaultSort: { column: 'lastExecution', direction: SORT.DESC },
        size: 3,
    },
    [JOB_CATEGORY.TOO_LONG]: {
        title: 'Jobs trop longs',
        color: ALERT_YELLOW,
        headers: TOO_LONG_HEADERS,
        defaultSort: { column: 'duration', direction: SORT.DESC },
        size: 12,
    },
    [JOB_CATEGORY.FULL_DIR]: {
        title: 'Répertoires remplis',
        color: PASTEL_COLOR.orange,
        headers: FULL_DIR_HEADERS,
        defaultSort: { column: 'count', direction: SORT.DESC },
        size: 3,
    },
}

const ERROR_LOGS_HEADERS = [
    { key: 'date', value: 'Date' },
    { key: 'message', value: 'Message' },
    { key: 'station', value: 'Station' },
]

const JobsTable = ({ instances = [], categoryFilter, jobTypeFilter }) => {
    const [selectedJob, setSelectedJob] = useState(null)
    const [errorLogs, setErrorLogs] = useState([])
    const [loadingErrors, setLoadingErrors] = useState(false)

    const closePanel = () => {
        setSelectedJob(null)
        setErrorLogs([])
        setLoadingErrors(false)
    }

    const notDeployedMessage = 'La récupération des logs n\'est pas encore déployée sur cette instance'

    const openErrorLogs = (row) => {
        // Reclic sur le même traitement : on referme le panneau
        if (selectedJob && selectedJob.instanceId === row.instanceId && selectedJob.jobId === row.jobId) {
            closePanel()
            return
        }
        if (!row.jobExecutionId || !row.instanceId) {
            toast.info(notDeployedMessage)
            window.open(row.url, '_blank')
            return
        }
        // On ouvre le panneau immédiatement (spinner via noDataDisplay le temps de la réponse back)
        setSelectedJob(row)
        setErrorLogs([])
        setLoadingErrors(true)
        aquaFetch(ApplicationConf.jobErrorLogs(), {
            method: 'POST',
            body: { instanceId: row.instanceId, jobId: row.jobId, executionId: row.jobExecutionId },
        })
            .then(res => {
                const logs = (res?.data ?? [])
                    .filter(d => d[0] === 'error')
                    .map(d => ({ date: d[1], value: d[3], station: d[4] }))
                // Rien ne revient : la route aquaparcOnly n'est pas déployée sur l'instance
                // → popup + redirection vers le traitement sur l'instance, pas de tableau
                if (!logs.length) {
                    toast.info(notDeployedMessage)
                    window.open(row.url, '_blank')
                    closePanel()
                    return
                }
                setErrorLogs(logs)
            })
            .catch(() => {
                toast.error('Impossible de récupérer les logs de cette instance')
                window.open(row.url, '_blank')
                closePanel()
            })
            .finally(() => setLoadingErrors(false))
    }

    const onRowClick = (category, row) => {
        if (category === JOB_CATEGORY.ERROR) {
            openErrorLogs(row)
        } else {
            window.open(row.url, '_blank')
        }
    }

    const rowsByCategory = useMemo(() => {
        const acc = {
            [JOB_CATEGORY.NOT_LAUNCHED]: [],
            [JOB_CATEGORY.ERROR]: [],
            [JOB_CATEGORY.NO_LOGS]: [],
            [JOB_CATEGORY.TOO_LONG]: [],
            [JOB_CATEGORY.FULL_DIR]: [],
        }
        const matchesJobType = j => !jobTypeFilter || j.jobType === jobTypeFilter
        instances.forEach(instance => {
            const jobs = instance.jobs
            if (!jobs) return
            jobs.notLaunchedJobs?.filter(matchesJobType).forEach(j => acc[JOB_CATEGORY.NOT_LAUNCHED].push(buildJobRow(instance, j)))
            // errorJobs backend = statuts 0/5/6 : status 0 = vraie erreur, le reste = jobs sans logs exploitables
            jobs.errorJobs?.filter(matchesJobType).forEach(j => acc[j.status === 0 ? JOB_CATEGORY.ERROR : JOB_CATEGORY.NO_LOGS].push(buildJobRow(instance, j)))
            jobs.tooLongJobs?.filter(matchesJobType).forEach(j => acc[JOB_CATEGORY.TOO_LONG].push(buildJobRow(instance, j)))
            if (!jobTypeFilter) {
                jobs.fullDirectories?.forEach(d => acc[JOB_CATEGORY.FULL_DIR].push(buildDirectoryRow(instance, d)))
            }
        })
        return acc
    }, [instances, jobTypeFilter])

    const isFiltered = useMemo(() => !!categoryFilter && !!TABLE_CONFIGS[categoryFilter], [categoryFilter])

    const renderTable = (category) => {
        const config = TABLE_CONFIGS[category]
        return (
            <CardTable
                title={config.title}
                color={config.color}
                rows={rowsByCategory[category]}
                headers={config.headers}
                defaultSort={config.defaultSort}
                rowsPerPageDefault={25}
                onClickRow={r => onRowClick(category, r)}
            />
        )
    }

    const errorPanel = (
        <CardTable
            title={`${selectedJob?.instance?.value ?? ''} - ${selectedJob?.name?.value ?? ''}`}
            color={ALERT_RED}
            rows={errorLogs.map(l => ({
                date: { value: l.date ? getFullDate(l.date) : '', sortValue: l.date ?? 0 },
                message: { value: l.value },
                station: { value: l.station },
            }))}
            headers={ERROR_LOGS_HEADERS}
            rowsPerPageDefault={25}
            actions={[
                {
                    icon: 'open_in_new',
                    tooltip: 'Voir le traitement sur l\'instance',
                    onClick: () => window.open(selectedJob.url, '_blank'),
                },
                {
                    icon: 'close',
                    tooltip: 'Fermer',
                    onClick: closePanel,
                },
            ]}
            noDataDisplay={loadingErrors ? <Box sx={{ display: 'flex', justifyContent: 'center', padding: 2 }}><CircularProgress size={28} /></Box> : undefined}
        />
    )

    // Vue filtrée (clic sur une catégorie de la cartographie) : un seul tableau
    if (isFiltered) {
        const isErrorPanel = categoryFilter === JOB_CATEGORY.ERROR && selectedJob
        return (
            <Grid container spacing={1.5}>
                <Grid size={isErrorPanel ? 6 : 12}>
                    {renderTable(categoryFilter)}
                </Grid>
                {isErrorPanel && <Grid size={6}>{errorPanel}</Grid>}
            </Grid>
        )
    }

    return (
        <Grid container spacing={1.5}>
            <Grid size={selectedJob ? 6 : 12}>
                {renderTable(JOB_CATEGORY.ERROR)}
            </Grid>
            {selectedJob && <Grid size={6}>{errorPanel}</Grid>}

            <Grid size={6}>
                {renderTable(JOB_CATEGORY.TOO_LONG)}
            </Grid>

            <Grid size={6} container spacing={1.5} sx={{ alignContent: 'flex-start' }}>
                <Grid size={12}>{renderTable(JOB_CATEGORY.NOT_LAUNCHED)}</Grid>
                {!jobTypeFilter && <Grid size={12}>{renderTable(JOB_CATEGORY.FULL_DIR)}</Grid>}
                <Grid size={12}>{renderTable(JOB_CATEGORY.NO_LOGS)}</Grid>
            </Grid>
        </Grid>
    )
}

JobsTable.propTypes = {
    instances: PropTypes.arrayOf(PropTypes.shape({
        instanceId: PropTypes.number,
        name: PropTypes.string,
        url: PropTypes.string,
        instanceType: PropTypes.string,
        jobs: PropTypes.shape({}),
    })),
    categoryFilter: PropTypes.oneOf(JOB_CATEGORIES.map(c => c.value)),
    jobTypeFilter: PropTypes.string,
}

export { JOB_CATEGORIES, JOB_CATEGORY }
export default JobsTable
